import React, { useState, useEffect, useDeferredValue } from "react";
import api from "../api/api";
import TableRenderer from "../components/ui/TableRenderer";
import { exportSectionsToCsv } from "../utils/exportToExcel";
import { formatDateDDMMYYYY } from "../utils/dateFormat";
import { getTotalsByColumn, isTotalsTable } from "../utils/tableTotals";
import "./SummaryRev.css";

const SummaryRev = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [popupLoading, setPopupLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [rowHeight, setRowHeight] = useState("compact");
  const [fullscreenTable, setFullscreenTable] = useState(null);
  const [fontScale, setFontScale] = useState(100);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const userName = sessionStorage.getItem("userName");
      const response = await api.GetBoEDatas(userName, "Summary Rev");
      const boEData = response?.BoEData || response;
      setData(boEData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleImportQtyKgsClick = async (boeNo) => {
    // Handler can be removed - now handled directly in table rendering
  };

  const handleTotalShippedMOOWRClick = async (boeNo) => {
    // Handler can be removed - now handled directly in table rendering
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupData(null);
    setPopupType(null);
  };

  const filterTableData = (tableData) => {
    if (!tableData || !deferredSearchQuery) return tableData;
    const normalizedSearch = deferredSearchQuery.toLowerCase();

    return tableData.filter((row) =>
      Object.values(row).some((value) =>
        value !== null && value !== undefined &&
        String(value).toLowerCase().includes(normalizedSearch)
      )
    );
  };

  const formatDate = (dateString) => {
    return formatDateDDMMYYYY(dateString);
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "number") return value.toLocaleString();
    return value;
  };

  const isNumericValue = (value) => {
    if (typeof value === "number") return Number.isFinite(value);
    if (typeof value !== "string") return false;
    const normalized = value.trim().replace(/,/g, "");
    return /^-?\d+(\.\d+)?$/.test(normalized);
  };

  const isColumnConfigTable = (table) => {
    return (
      Array.isArray(table) &&
      table.length > 0 &&
      ["DbColDisplay", "DisplayColName", "Visible", "Priority"].every((key) => key in table[0])
    );
  };

  const getOrderedColumns = (tableData, columnConfig) => {
    const defaultColumns = Object.keys(tableData[0] || {});
    if (!columnConfig) return defaultColumns;

    const configuredColumns = new Set(
      columnConfig
        .filter((cfg) => cfg.DbColDisplay)
        .map((cfg) => cfg.DbColDisplay)
    );

    const configColumns = columnConfig
      .filter((cfg) => Number(cfg.Visible) !== 0 && cfg.DbColDisplay)
      .sort((a, b) => Number(a.Priority || 9999) - Number(b.Priority || 9999))
      .map((cfg) => cfg.DbColDisplay)
      .filter((col) => defaultColumns.includes(col));

    const remainingColumns = defaultColumns.filter((col) => !configuredColumns.has(col));
    return [...configColumns, ...remainingColumns];
  };

  const getDisplayNameMap = (columnConfig) => {
    if (!columnConfig) return {};
    return columnConfig.reduce((acc, cfg) => {
      if (cfg.DbColDisplay) acc[cfg.DbColDisplay] = cfg.DisplayColName || cfg.DbColDisplay;
      return acc;
    }, {});
  };

  const openBoeDetailWindow = (boeNo) => {
    if (!boeNo) return;
    const detailUrl = `${window.location.origin}/boe-detail?boeNo=${boeNo}&type=Summary Rev`;
    window.open(detailUrl, `boe-${boeNo}`, "width=1400,height=900,scrollbars=yes");
  };

  const handleExportToExcel = () => {
    if (!data) return;

    const sections = [];
    const addSection = (tableRows, title, columnConfig = null) => {
      const filteredRows = filterTableData(tableRows);
      if (!filteredRows || filteredRows.length === 0) return;

      const columns = columnConfig
        ? getOrderedColumns(filteredRows, columnConfig)
        : Object.keys(filteredRows[0] || {});
      if (!columns.length) return;

      const displayNameMap = columnConfig ? getDisplayNameMap(columnConfig) : {};
      const headers = columns.map((col) => displayNameMap[col] || col.replace(/_/g, " "));
      const records = filteredRows.map((row) =>
        columns.map((col) => {
          const value = row[col];
          if (col.toLowerCase().includes("date")) return formatDateDDMMYYYY(value);
          if (value === null || value === undefined) return "-";
          return value;
        })
      );

      sections.push({ title, headers, records });
    };

    const mainConfig = isColumnConfigTable(data.Table1) ? data.Table1 : null;
    addSection(data.Table, "Summary Rev", mainConfig);
    if (!isColumnConfigTable(data.Table1)) addSection(data.Table1, "Additional Details");
    addSection(data.Table2, "Related Records");
    addSection(data.Table3, "More Information");
    addSection(data.Table4, "Extra Data");

    exportSectionsToCsv({ sections, fileNamePrefix: "Summary_Rev" });
  };

  const renderRecordAsCard = (record) => {
    if (!record) return null;

    const entries = Object.entries(record);

    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow p-1.5 border border-blue-100 mb-2">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-1.5">
          {entries.map(([key, value]) => {
            let displayValue = value;
            
            if (value === null || value === undefined) {
              displayValue = "-";
            } else if (typeof value === "number") {
              displayValue = value.toLocaleString();
            } else if (key.toLowerCase().includes("date")) {
              displayValue = formatDateDDMMYYYY(value);
            }

            return (
              <div key={key} className="flex items-center">
                <span className="text-xs font-bold text-gray-700 mr-1 min-w-fit">
                  {key.replace(/_/g, " ")}:
                </span>
                <span className={`text-xs text-gray-800 break-words ${typeof value === 'number' ? 'text-right' : ''}`}>
                  {displayValue}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTableWithClickable = (
    tableData,
    title,
    tableKey = null,
    clickableRevBoe = false,
    columnConfig = null,
    isFullscreen = false,
    totalsTable = null
  ) => {
    if (!tableData || tableData.length === 0) {
      return (
        <div className="summary-empty-panel">
          <p>No data available</p>
        </div>
      );
    }

    const columns = getOrderedColumns(tableData, columnConfig);
    const displayNameMap = getDisplayNameMap(columnConfig);
    const cellPaddingStyle = rowHeight === "compact" 
      ? { paddingLeft: "6px", paddingRight: "6px", paddingTop: "3px", paddingBottom: "3px" }
      : { paddingLeft: "16px", paddingRight: "16px", paddingTop: "8px", paddingBottom: "8px" };
    const headerCellStyle = {
      paddingLeft: "8px",
      paddingRight: "8px",
      paddingTop: "10px",
      paddingBottom: "10px",
      minHeight: "52px",
      lineHeight: "1.2",
    };
    const numericColumns = new Set(
      columns.filter((col) => tableData.some((row) => isNumericValue(row[col])))
    );
    const totalsByColumn = getTotalsByColumn(totalsTable, columns);

    return (
      <div className={`summary-table-panel ${isFullscreen ? "is-fullscreen" : ""}`}>
        <div className={`summary-table-wrap ${isFullscreen ? "max-h-[calc(100vh-56px)]" : "max-h-[76vh]"}`}>
          <table className="summary-grid" style={{ borderSpacing: "0", fontSize: `${fontScale}%` }}>
            <thead>
              <tr>
                {columns.map((col) => {
                  const isNumeric = numericColumns.has(col);
                  const displayLabel = displayNameMap[col] || col.replace(/_/g, " ");
                  const isBoeSlNo = displayLabel.replace(/[_\s]/g, "").toLowerCase() === "boeslno";
                  const totalValue = totalsByColumn[col];
                  return (
                    <th
                      key={col}
                    className={`whitespace-normal break-words align-middle ${
                        isBoeSlNo ? "text-center" : isNumeric ? "text-right" : "text-left"
                      }`}
                      style={headerCellStyle}
                    >
                      <div className="flex flex-col">
                        <span className="leading-tight">{displayLabel}</span>
                        <span
                          className={`text-sm font-medium leading-tight min-h-[18px] ${
                            totalValue !== undefined ? "text-emerald-700" : "text-transparent"
                          }`}
                        >
                          {totalValue !== undefined ? totalValue : "\u00A0"}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, i) => (
                <tr
                  key={i}
                  onClick={() => setSelectedRowIndex(selectedRowIndex === i ? null : i)}
                  className={selectedRowIndex === i ? "is-selected" : ""}
                  style={{ height: rowHeight === "compact" ? "auto" : "auto" }}
                >
                  {columns.map((col) => {
                    const isNumeric = numericColumns.has(col);
                    const displayLabel = displayNameMap[col] || col.replace(/_/g, " ");
                    const isBoeSlNo = displayLabel.replace(/[_\s]/g, "").toLowerCase() === "boeslno";
                    let value = row[col];
                    let wasNumeric = isNumericValue(row[col]);

                    // Rev_BOE Column - Load selected BOE detail below table
                    if (clickableRevBoe && (col === "Rev_BOE" || col === "Rev BOE")) {
                      return (
                        <td
                          key={col}
                          className="whitespace-nowrap summary-link"
                          style={cellPaddingStyle}
                          onClick={(e) => {
                            e.stopPropagation();
                            const boeNo = String(value);
                            openBoeDetailWindow(boeNo);
                          }}
                        >
                          {value}
                        </td>
                      );
                    }

                    if (col.toLowerCase().includes("date")) {
                      value = formatDate(value);
                    } else if (typeof value === "number") {
                      value = formatNumber(value);
                    } else if (value === null) {
                      value = "-";
                    }

                    return (
                      <td
                        key={col}
                        className={`whitespace-nowrap ${
                          isBoeSlNo ? "text-center" : isNumeric || wasNumeric ? "text-right" : "text-left"
                        }`}
                        style={cellPaddingStyle}
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="summary-page">
      <div className="summary-shell">
        <div className="summary-topbar">
          <div className="summary-title">Summary Rev</div>
          <span className="summary-topbar-text">{new Date().toLocaleString()}</span>
        </div>

        <div className="summary-content">
          <div className="summary-toolbar">
            <div className="summary-toolbar-left">
              <input
                type="text"
                placeholder="Search records..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="summary-search"
              />

            <button
              onClick={() => setRowHeight(rowHeight === "compact" ? "normal" : "compact")}
              className={`summary-btn ${rowHeight === "compact" ? "is-active" : ""}`}
              title="Toggle Row Height"
            >
              {rowHeight === "compact" ? "Compact" : "Normal"}
            </button>
            </div>
            <div className="summary-toolbar-right">
              <button
                onClick={() => setFontScale((prev) => Math.max(80, prev - 10))}
                className="summary-btn"
                title="Decrease Font Size"
              >
                A-
              </button>
              <button
                onClick={() => setFontScale((prev) => Math.min(140, prev + 10))}
                className="summary-btn"
                title="Increase Font Size"
              >
                A+
              </button>
              <button
                onClick={handleExportToExcel}
                className="summary-btn"
                title="Export to Excel"
              >
                Export
              </button>
              <button
                onClick={() => setFullscreenTable(fullscreenTable === "table1" ? null : "table1")}
                className="summary-btn"
                title="Toggle Fullscreen"
              >
                {fullscreenTable === "table1" ? "Exit Full" : "Full"}
              </button>
            </div>
          </div>

          {loading && (
            <div className="summary-loader-panel">
              <div className="summary-spinner"></div>
            </div>
          )}

          {!loading && data && (
            <div className="summary-body">
              <div className="summary-stack">
                {(() => {
                  const mainConfig = isColumnConfigTable(data.Table1) ? data.Table1 : null;
                  const mainColumns = data.Table?.length ? getOrderedColumns(data.Table, mainConfig) : [];
                  const mainTotals = isTotalsTable(data.Table2, mainColumns) ? data.Table2 : null;

                  return (
                    <>
                      {data.Table && renderTableWithClickable(
                        filterTableData(data.Table),
                        "Summary Rev",
                        "table1",
                        true,
                        mainConfig,
                        false,
                        mainTotals
                      )}
                      {data.Table1 && !isColumnConfigTable(data.Table1) && renderTableWithClickable(filterTableData(data.Table1), "Additional Details", "table2")}
                      {data.Table2 && !mainTotals && renderTableWithClickable(filterTableData(data.Table2), "Related Records", "table3")}
                      {data.Table3 && renderTableWithClickable(filterTableData(data.Table3), "More Information", "table4")}
                      {data.Table4 && renderTableWithClickable(filterTableData(data.Table4), "Extra Data", "table5")}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {!loading && !data && (
            <div className="summary-body">
              <div className="summary-empty-panel">
                <p>No data available</p>
              </div>
            </div>
          )}
          </div>
      </div>

      {showPopup && (
        <div className="summary-modal">
          <div 
            className="summary-modal-backdrop"
            onClick={closePopup}
          ></div>
          
          <div className="summary-modal-card">
            <div className="summary-modal-header">
              <h2 className="summary-modal-title">
                {popupType === "import" ? "Import Qty Kgs Details" : "Total Shipped MOOWR Details"}
              </h2>
              <button 
                onClick={closePopup}
                className="summary-icon-btn"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="summary-modal-body">
              {popupLoading ? (
                <div className="summary-loader-panel">
                  <div className="summary-spinner"></div>
                </div>
              ) : popupData ? (
                <div className="space-y-4">
                  {popupType === "import" ? (
                    <>
                      {popupData.Table && popupData.Table.length > 0 && (
                        <>
                          {renderRecordAsCard(popupData.Table[0])}
                          {popupData.Table.length > 1 && renderTableWithClickable(popupData.Table, "Import Qty Kgs Records")}
                        </>
                      )}
                      {popupData.Table1 && renderTableWithClickable(popupData.Table1, "Additional Details")}
                      {popupData.Table2 && renderTableWithClickable(popupData.Table2, "Related Information")}
                    </>
                  ) : (
                    <>
                      {popupData.Table && popupData.Table.length > 0 && (
                        <>
                          {renderRecordAsCard(popupData.Table[0])}
                          {popupData.Table.length > 1 && renderTableWithClickable(popupData.Table, "Sales Shipped under MOOWR")}
                        </>
                      )}
                      {popupData.Table1 && renderTableWithClickable(popupData.Table1, "Additional Details")}
                      {popupData.Table2 && renderTableWithClickable(popupData.Table2, "Related Information")}
                    </>
                  )}
                </div>
              ) : (
                <div className="summary-empty-panel">
                  <p>No data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {fullscreenTable && (
        <div className="detail-page detail-overlay">
          <div className="detail-header">
            <h2 className="detail-header-title">Expanded View</h2>
            <button
              type="button"
              onClick={() => setFullscreenTable(null)}
               className="summary-btn"
              title="Close Expanded View"
            >        
              Close
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            {fullscreenTable === "table1" && data?.Table && (() => {
              const mainConfig = isColumnConfigTable(data.Table1) ? data.Table1 : null;
              const mainColumns = data.Table?.length ? getOrderedColumns(data.Table, mainConfig) : [];
              const mainTotals = isTotalsTable(data.Table2, mainColumns) ? data.Table2 : null;
              return renderTableWithClickable(
                filterTableData(data.Table),
                "Summary Rev",
                "table1",
                false,
                mainConfig,
                true,
                mainTotals
              );
            })()}
            {fullscreenTable === "table2" && data?.Table1 && !isColumnConfigTable(data.Table1) && renderTableWithClickable(filterTableData(data.Table1), "Additional Details", "table2", false, null, true)}
            {fullscreenTable === "table3" && data?.Table2 && !isTotalsTable(data.Table2, data.Table?.length ? getOrderedColumns(data.Table, isColumnConfigTable(data.Table1) ? data.Table1 : null) : []) && renderTableWithClickable(filterTableData(data.Table2), "Related Records", "table3", false, null, true)}
            {fullscreenTable === "table4" && data?.Table3 && renderTableWithClickable(filterTableData(data.Table3), "More Information", "table4", false, null, true)}
            {fullscreenTable === "table5" && data?.Table4 && renderTableWithClickable(filterTableData(data.Table4), "Extra Data", "table5", false, null, true)}
          </div>
        </div>
      )}
    </div>
  );
};

export default SummaryRev;






